import {
  getSalesForceListByConuntry,
  uploadPromotionImage,
  createPromotion,
  updatePromotion,
  getPromotionDetail,
  getPromotionCategory,
  getPromotionMonthlyUsage,
} from '@/api/farah'

const getDefaultState = () => {
  return {
    branches: [],
    promotionCategory: {},
    promotionData: {},
    promotionDetailData: {},
    selectedBranchesByCountry: [],
    isFetchingBranches: false,
    isFetchingDetail: false,
    isFetchingPromotionCategory: false,
    isSubmitingPromotion: false,
    lastError: undefined,
    promotionFormMode: null,
    promotionMonthlyUsage: {},
  }
}

const mutations = {
  SET_BRANCHES(state, { data }) {
    state.branches = data
  },
  SET_PROMOTION_CATEGORY(state, { data }) {
    state.promotionCategory = data
  },
  SET_PROMOTION_DATA(state, data) {
    state.promotionData = { ...state.promotionData, ...data }
  },
  SET_PROMOTION_DETAIL_DATA(state, { data }) {
    state.promotionDetailData = { ...state.promotionDetailData, ...data }
  },
  SET_SELECTED_BRANCHES_BY_COUNTRY(state, data) {
    state.selectedBranchesByCountry = data
  },
  SET_IS_FETCHING(state, { name, value }) {
    state[name] = value
  },
  SET_IS_SUBMITING(state, { name, value }) {
    state[name] = value
  },
  SET_ERROR(state, error) {
    state.lastError = error
  },
  SET_PROMOTION_FORM_MODE(state, { data }) {
    state.promotionFormMode = data
  },
  SET_PROMOTION_MONTHLY_USAGE(state, { data }) {
    state.promotionMonthlyUsage = data
  },
  RESET_ALL_DATA(state) {
    // Advertiser should be kept between pages
    Object.assign(state, { ...getDefaultState() })
  },
}

const getters = {
  branches: (state) => state.branches,
  promotionCategory: (state) => state.promotionCategory,
  checkedCountry(state) {
    const country = state.promotionData.checkedCountry || ''
    return country.charAt(0).toLowerCase() + country.slice(1)
  },
  branchesByCountry(state, getters) {
    const country = getters.checkedCountry || ''
    return state.branches[country] || []
  },
  isFetchingBranches: (state) => state.isFetchingBranches,
  isFetchingDetail: (state) => state.isFetchingDetail,
  promotionData: (state) => state.promotionData,
  promotionDetailData: (state) => state.promotionDetailData,
  selectedBranchesByCountry: (state) => state.selectedBranchesByCountry,
  promotionMonthlyUsage: (state) => state.promotionMonthlyUsage,
  isSubmitingPromotion: (state) => state.isSubmitingPromotion,
  lastError: (state) => state.lastError,
}

const actions = {
  async fetchBranches({ commit }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingBranches',
        value: true,
      })
      const res = await getSalesForceListByConuntry()

      commit('SET_BRANCHES', { data: res?.data?.data })

      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingBranches',
        value: false,
      })
    }
  },
  async fetchPromotionCategory({ commit }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPromotionCategory',
        value: true,
      })
      const res = await getPromotionCategory()
      if (res?.data?.data) {
        commit('SET_PROMOTION_CATEGORY', { data: res?.data?.data })
        return res?.data?.data
      }
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingPromotionCategory',
        value: false,
      })
    }
  },
  async fetchPromotionDetail({ commit }, { promotionId }) {
    try {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: true,
      })
      const res = await getPromotionDetail(promotionId)
      if (res?.data?.data) {
        commit('SET_PROMOTION_DETAIL_DATA', { data: res?.data?.data })
        return res?.data?.data
      }
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_FETCHING', {
        name: 'isFetchingDetail',
        value: false,
      })
    }
  },
  setPromotionData({ commit }, data) {
    commit('SET_PROMOTION_DATA', data)
  },
  setSelectedBranchesByCountry({ commit }, data) {
    commit('SET_SELECTED_BRANCHES_BY_COUNTRY', data)
  },
  setPromotionFormMode({ commit }, data) {
    commit('SET_PROMOTION_FORM_MODE', { data })
  },
  resetAllData({ commit }) {
    commit('RESET_ALL_DATA')
  },
  async submitPromotion({ commit }, { data }) {
    try {
      commit('SET_IS_SUBMITING', {
        name: 'isSubmitingPromotion',
        value: true,
      })
      const res = await createPromotion(data)
      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_SUBMITING', {
        name: 'isSubmitingPromotion',
        value: false,
      })
    }
  },
  async updatePromotion({ commit }, { data }) {
    const { promotionId, data: submitData } = data

    try {
      commit('SET_IS_SUBMITING', {
        name: 'isSubmitingPromotion',
        value: true,
      })
      const res = await updatePromotion(promotionId, submitData)
      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    } finally {
      commit('SET_IS_SUBMITING', {
        name: 'isSubmitingPromotion',
        value: false,
      })
    }
  },
  async uploadPromotionImage({ commit }, { data }) {
    const { promotionId, data: submitData } = data

    try {
      const res = await uploadPromotionImage(promotionId, submitData)
      return res?.data?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    }
  },
  async fetchPromotionMonthlyUsage({ commit }, { page, pageSize }) {
    try {
      const res = await getPromotionMonthlyUsage({ page, pageSize })
      commit('SET_PROMOTION_MONTHLY_USAGE', { data: res?.data })

      return res?.data
    } catch (error) {
      commit('SET_ERROR', error?.response?.data)
      throw error
    }
  },
}

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations,
  actions,
  getters,
}
